import { Trans, useLingui } from "@lingui/react/macro"
import type { CallSnapshot } from "@/types/v2/voice"
import { initials } from "@/lib/initials"
import { PhoneSolidIcon, PhoneDownSolidIcon, SparkleIcon } from "./icons"

interface IncomingCallSheetProps {
  call: CallSnapshot
  onAnswer: () => void
  onDecline: () => void
}

// The ringing-call prompt, dark (CON-155, frame 11). Audio-
// only: caller identity, an "AI will transcribe this call"
// cue, and big Decline (red) / Answer (green) circles. Caller
// name comes from the ActionCable broadcast (contact) when
// available, falling back to the SDK's remote display name,
// then the raw number.
export function IncomingCallSheet({
  call,
  onAnswer,
  onDecline,
}: IncomingCallSheetProps) {
  const { t } = useLingui()
  const isOutbound = call.voiceCall?.direction === "outbound"
  const heading = isOutbound ? t`Connecting call` : t`Incoming call`
  const name = callerName(call, t`Unknown caller`)

  return (
    <div className="flex flex-col gap-4 px-[18px] py-5">
      <div className="flex items-center gap-3">
        <div className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-[#3A352E] text-[14px] font-semibold leading-[18px] text-[#F0EDE6]">
          {initials(name)}
        </div>
        <div className="flex min-w-0 flex-col gap-[3px]">
          <span className="text-[11px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-[#9A938A]">
            {heading}
          </span>
          <span className="truncate text-[15px] font-semibold leading-[18px] text-[#FBF7F0]">
            {name}
          </span>
          {callerSubtitle(call) ? (
            <span className="truncate text-[12px] leading-4 text-[#9A938A]">
              {callerSubtitle(call)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2.5 rounded-[11px] bg-[#2A2620] px-3 py-[9px]">
        <SparkleIcon
          width={13}
          height={13}
          className="shrink-0 text-[#E0A56A]"
        />
        <span className="text-[12px] font-medium leading-4 text-[#E0A56A]">
          <Trans>AI will transcribe this call</Trans>
        </span>
      </div>

      <div className="flex items-center justify-center gap-9 pt-0.5">
        <div className="flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={onDecline}
            aria-label={t`Decline call`}
            className="flex size-[54px] items-center justify-center rounded-full bg-[#E5484D] text-white transition-colors hover:bg-[#D63B40] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5484D]/50"
          >
            <PhoneDownSolidIcon width={22} height={22} />
          </button>
          <span className="text-[11px] leading-[14px] text-[#9A938A]">
            <Trans>Decline</Trans>
          </span>
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={onAnswer}
            aria-label={t`Answer call`}
            className="flex size-[54px] items-center justify-center rounded-full bg-[#1FAA53] text-white transition-colors hover:bg-[#1B9549] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1FAA53]/50"
          >
            <PhoneSolidIcon width={22} height={22} />
          </button>
          <span className="text-[11px] leading-[14px] text-[#9A938A]">
            <Trans>Answer</Trans>
          </span>
        </div>
      </div>
    </div>
  )
}

function callerName(call: CallSnapshot, unknownFallback: string): string {
  return (
    call.voiceCall?.contact?.name ||
    call.remoteDisplayName ||
    numberFor(call) ||
    unknownFallback
  )
}

function callerSubtitle(call: CallSnapshot): string | null {
  const named = call.voiceCall?.contact?.name || call.remoteDisplayName
  const number = numberFor(call)
  // Only show the number as a subtitle when we already have
  // a name on the headline (so we don't repeat it).
  return named && number ? number : null
}

function numberFor(call: CallSnapshot): string | null {
  const vc = call.voiceCall
  if (!vc) return null
  return vc.direction === "outbound" ? vc.calleeNumber : vc.callerNumber
}
